<script>
	import { each } from "svelte/internal";
	import { format, formatLocale } from "d3-format";

	// UK locale so "$" in a format string renders as £ rather than $
	const ukLocale = formatLocale({
		decimal: ".",
		thousands: ",",
		grouping: [3],
		currency: ["£", ""],
	});

	let ukFormat = ukLocale.format("$,.0f");

	export let type = "line";
	export let id;
	export let row;
	export let dataCharts;

	$: key = row.measure;

	$: thisChart = dataCharts.map((e) => e[key]).filter((e) => e);
	$: max = row.yaxisend;
	$: min = row.yaxisstart;
	$: range = max - min;

	let chartWidth = 300,
		height = 110,
		offset = 35,
		rightPosition = 74;

	//Manually setting the margins where needed
	let offsets = [
		["satisRship", 50],
		["depressionAnxiety", 50],
		["sportsPart", 50],
		["crime", 50],
		["diffFinance", 50],
		["genderPaygap", 50],
		["hholdIncome", 55],
		["hholdWealth", 65],
		["incomeInequal", 50],
		["lowIncome", 50],
		["noQuals", 50],
		["unemployment", 50],
		["trustOthers", 50],
		["relyOn", 50],
		["volunteering", 50],
		["artsCulture", 50],
		["belongNeigh", 50],
		["voice", 50],
		["GDP", 50],
		["hopeFuture", 50],
		["fairTreatment", 50],
		["satisSkills", 63],
		["satisSocial", 50],
		["satisAccomm", 50],
		["satisLocal", 50],
		["satisHealth", 50],
		["satisHealthcare", 50],
		["satisTime", 50],
		["satisJob", 50],
		["satisEducation", 50],
		["satisPolice", 50],
		["satisCourts", 50],
		["greenhouseGas", 40],
		["crime", 65],
		["socialSurveyResponse", 50],
		["businessSurveyResponse", 52]
	];

	let rightPositions = [
		["genderPaygap", 80],
		["hholdWealth", 90],
		["hholdIncome", 80],
		["greenhouseGas", 85],
		["unemployment", 85],
		["satisLocal", 90],
		["voice", 85],
		["satisTime", 90],
		["genderPaygap", 85], 
		["crime", 85],
		["hholdRecycling", 80]
	];

	$: {
		offsets.forEach((d) => {
			d[0] == key ? (offset = d[1]) : (offset = offset);
		});

		rightPositions.forEach((d) => {
			d[0] == key
				? (rightPosition = d[1])
				: (rightPosition = rightPosition);
		});
	}

	$: width = chartWidth - offset - rightPosition;

	$: decimalPlacesRaw = row?.decimalPlaces ?? row?.decimalPlace;
	$: decimalPlaces = Number.isFinite(+decimalPlacesRaw)
		? Math.max(0, Math.min(20, Math.trunc(+decimalPlacesRaw)))
		: 1;

	$: ukFormat = ukLocale.format(`$,.${decimalPlaces}f`);

	function formatWithDecimalPlaces(value) {
		const numberValue = +value;
		return Number.isFinite(numberValue)
			? numberValue.toFixed(decimalPlaces)
			: value;
	}

	let el;

	$: textLabel =
		key == "humanCapital"
			? "£" +
				formatWithDecimalPlaces(thisChart[thisChart.length - 1]) +
				" " +
				row.directLabelUnit
			: row.directLabelUnit?.includes("£")
				? ukFormat(thisChart[thisChart.length - 1])
				: row.directLabelUnit?.includes("%")
					? formatWithDecimalPlaces(thisChart[thisChart.length - 1]) +
						row.directLabelUnit
					: row.directLabelUnit
						? formatWithDecimalPlaces(thisChart[thisChart.length - 1]) +
							" " +
							row.directLabelUnit
						: key == "hholdIncome" || key == "hholdWealth"
							? ukFormat(thisChart[thisChart.length - 1])
							: thisChart[thisChart.length - 1];

	// Reformats mmm-yy strings (e.g. "Jan-25") to "Jan 2025" format
	function formatDate(dateStr) {
		if (!dateStr) return dateStr;

		const str = String(dateStr);

		const monthMap = {
			jan: "Jan",
			feb: "Feb",
			mar: "Mar",
			apr: "Apr",
			may: "May",
			jun: "June",
			jul: "July",
			aug: "Aug",
			sep: "Sept",
			oct: "Oct",
			nov: "Nov",
			dec: "Dec",
		};

		// Handle dd-mmm-yy format (e.g. "15-Jan-25") → "15 Jan 2025"
		const longMatch = str.match(/^(\d{1,2})-([A-Za-z]+)-(\d{2})$/);
		if (longMatch) {
			const month = monthMap[longMatch[2].toLowerCase()];
			const year = 2000 + parseInt(longMatch[3]);
			if (!month) return str;
			return `${parseInt(longMatch[1])} ${month} ${year}`;
		}

		// Handle mmm-yy format (e.g. "Jan-25") → "Jan 2025"
		const shortMatch = str.match(/^([A-Za-z]+)-(\d{2})$/);
		if (shortMatch) {
			const month = monthMap[shortMatch[1].toLowerCase()];
			const year =
				parseInt(shortMatch[2]) >= 30
					? 1900 + parseInt(shortMatch[2])
					: 2000 + parseInt(shortMatch[2]);
			if (!month) return str;
			return `${month} ${year}`;
		}

		return str;
	}

	$: formattedStartDate = formatDate(row.startdate);
	$: formattedEndDate = formatDate(row.enddate);
	$: formattedUpdated = formatDate(row.update);
	$: textLabelWords = String(textLabel ?? "").split(" ");
</script>

<div class="graphic" {id} bind:this={el} bind:clientWidth={chartWidth}>
	<!-- Chart heading text -->
	<div class="chartContainer">
		<p class="headingText">{@html row.headingtext}</p>
	</div>

	<svg class="chartDrawing" width="281">
		<!-- This bit draws the chart background -->
		<rect class="chartBackground" x={offset} {width} {height}></rect>
		<!-- Y-axis -->
		<line
			x1={offset}
			x2={offset}
			y1="0"
			y2={height}
			stroke="black"
			stroke-width="0.5px"
		/>
		<line x1={offset - 5} x2={offset} y1="0" y2="0" stroke="grey" />
		<line
			x1={offset - 5}
			x2={offset}
			y1={height}
			y2={height}
			stroke="grey"
		/>
		<text
			x={offset - 5}
			y="0"
			fill="black"
			text-anchor="end"
			dominant-baseline="middle">{format(",.0f")(row.yaxisend)}</text
		>
		<text
			x={offset - 5}
			y={height}
			fill="black"
			text-anchor="end"
			dominant-baseline="middle">{format(",.0f")(row.yaxisstart)}</text
		>

		<!-- X-axis -->
		<!-- X-axis line (if desired)-->
		{#if !row.yaxisstart}
			<line
				x1={offset}
				x2={offset + width}
				y1={height}
				y2={height}
				stroke="lightgrey"
				stroke-width="3px"
			/>
		{/if}

		<line
			x1={offset + width}
			x2={offset + width}
			y1={height}
			y2="115"
			stroke="grey"
		/>
		<line x1={offset} x2={offset} y1={height} y2="115" stroke="grey" />
		<text
			class="startDate"
			x={offset}
			y={height}
			dy="1.5em"
			dominant-baseline="top">{formattedStartDate}</text
		>
		<text
			class="endDate"
			x={offset + width}
			y={height}
			dy="1.5em"
			dominant-baseline="top">{formattedEndDate}</text
		>

		<!-- Blue data line-->
		{#each thisChart as point, i}
			<!-- Don't draw lines for missing values -->
			{#if +thisChart[i - 1] && +thisChart[i]}
				<line
					x1={(i - 1) * (width / (thisChart.length - 1)) + offset}
					x2={i * (width / (thisChart.length - 1)) + offset}
					y1={height - ((thisChart[i - 1] - min) / range) * height}
					y2={height - ((thisChart[i] - min) / range) * height}
					stroke="rgb(32,96,149)"
					stroke-width="2.5px"
					stroke-linecap="round"
				/>
				<!-- Add a grey band for missing values -->
			{:else if i !== 0}
				<rect
					x={(i - 1) * (width / (thisChart.length - 1)) + offset}
					y={0}
					width={width / (thisChart.length - 1)}
					{height}
					fill="rgba(217, 217, 217,0.5)"
				/>
			{/if}
		{/each}

		<!--dot and text label-->
		<circle
			cx={offset + width}
			cy={height -
				((thisChart[thisChart.length - 1] - min) / range) * height}
			r="3"
			fill="#206095"
		></circle>
		{#each textLabelWords as word, i}
			<text
				class="endText"
				x={offset + width - 10}
				y={height -
					((thisChart[thisChart.length - 1] - min) / range) * height}
				dy={14 * (i - (textLabelWords.length - 1) / 2)}
				fill="rgb(51, 51, 51)"
				text-anchor="start"
				dx="1em"
				dominant-baseline="middle"
				font-weight={i !== 0 ? 400 : 700}>{word}</text
			>
		{/each}
	</svg>
	<p class="updated">Updated: {formattedUpdated}</p>
</div>
